import React, { useCallback, useEffect, useMemo, useState } from "react";

import api from "../../api/client";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useNavigate, useParams } from "react-router-dom";

import "./EditClient.css";

/* ============================================================
   TYPES
============================================================ */

type MaidStatus = "not_arrived" | "arrived";

type Maid = {
  maid_id: number;
  maid_name: string;
  agent_name: string | null;
  status: MaidStatus;
  arrival_date: string | null;
  reminder_2_months: string | null;
  reminder_14_months: string | null;
  insurance_start_date: string | null;
  insurance_renewal_date: string | null;
  net_cost: number | null;
  price: number | null;
};

type Payment = {
  payment_id: number;
  amount: number;
  payment_date: string;
  created_at: string | null;
};

type PaymentSummary = {
  maid_id: number;
  maid_name: string;
  net_cost: number | null;
  price: number | null;
  profit: number | null;
  total_paid: number;
  amount_due: number | null;
  payments: Payment[];
};

type PaymentForm = {
  amount: string;
  payment_date: string;
};

type ClientDetail = {
  client_id: number;
  client_name: string;
  contact_info: string;
  maids: Maid[];
};

type ClientDocument = {
  document_id: number;
  original_filename: string;
  uploaded_at: string | null;
};

type DocumentEdit = {
  editing: boolean;
  name: string;
};

type MaidEdit = {
  editing: boolean;
  maid_name: string;
  agent_name: string;
  arrival_date: string;
  insurance_start_date: string;
};

/* ============================================================
   COMPONENT
============================================================ */

const EditClient: React.FC = () => {
  const token = useSelector((s: RootState) => s.user.token);

  const reduxClientId = useSelector((s: RootState) => s.user.clientId);

  const { id } = useParams();

  const clientId = Number(id ?? reduxClientId);

  const navigate = useNavigate();

  /* ============================================================
     GENERAL STATE
  ============================================================ */

  const [loading, setLoading] = useState(true);

  const [message, setMessage] = useState("");

  /* ============================================================
     CLIENT STATE
  ============================================================ */

  const [client, setClient] = useState<ClientDetail | null>(null);

  const [editingClient, setEditingClient] = useState(false);

  const [clientName, setClientName] = useState("");

  const [contactInfo, setContactInfo] = useState("");

  /* ============================================================
     MAID STATE
  ============================================================ */

  const [maidEdits, setMaidEdits] = useState<Record<number, MaidEdit>>({});

  // Controls which maid card is expanded.
  const [expandedMaidId, setExpandedMaidId] = useState<number | null>(null);

  /* ============================================================
     PAYMENT STATE
  ============================================================ */

  const [paymentSummaries, setPaymentSummaries] = useState<
    Record<number, PaymentSummary>
  >({});

  const [netCostInputs, setNetCostInputs] = useState<Record<number, string>>(
    {}
  );

  const [priceInputs, setPriceInputs] = useState<Record<number, string>>({});

  const [paymentForms, setPaymentForms] = useState<Record<number, PaymentForm>>(
    {}
  );

  const [paymentLoading, setPaymentLoading] = useState<Record<number, boolean>>(
    {}
  );

  /* ============================================================
     ADD MAID STATE
  ============================================================ */

  const [showAddMaid, setShowAddMaid] = useState(false);

  const [newMaidName, setNewMaidName] = useState("");

  const [newAgentName, setNewAgentName] = useState("");

  const [newArrivalDate, setNewArrivalDate] = useState("");

  const [newInsuranceStartDate, setNewInsuranceStartDate] = useState("");

  const [addingMaid, setAddingMaid] = useState(false);

  /* ============================================================
     DOCUMENT STATE
  ============================================================ */

  const [documents, setDocuments] = useState<ClientDocument[]>([]);

  const [documentFiles, setDocumentFiles] = useState<File[]>([]);

  const [uploadingDocuments, setUploadingDocuments] = useState(false);

  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const [documentEdits, setDocumentEdits] = useState<
    Record<number, DocumentEdit>
  >({});

  /* ============================================================
     MAIDS DISPLAY
  ============================================================ */

  const newestFirstMaids = useMemo(
    () => (client?.maids ?? []).slice(),
    [client]
  );

  /* ============================================================
     LOAD DOCUMENTS
  ============================================================ */

  const loadDocuments = useCallback(async () => {
    if (!clientId || Number.isNaN(clientId)) {
      return;
    }

    try {
      setLoadingDocuments(true);

      const res = await api.get(`/clients/${clientId}/documents`, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });

      setDocuments(res.data.documents || []);
    } catch (err: any) {
      console.error("Failed to load documents:", err);

      setMessage(
        err?.response?.data?.message || "Failed to load client documents."
      );
    } finally {
      setLoadingDocuments(false);
    }
  }, [clientId, token]);

  /* ============================================================
     LOAD PAYMENTS
  ============================================================ */

  const loadPayments = useCallback(
    async (maidId: number) => {
      if (!clientId || Number.isNaN(clientId) || !token) {
        return;
      }

      try {
        const response = await api.get(
          `/clients/${clientId}/maids/${maidId}/payments`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const summary: PaymentSummary = response.data;

        setPaymentSummaries((prev) => ({
          ...prev,
          [maidId]: summary,
        }));

        setNetCostInputs((prev) => ({
          ...prev,
          [maidId]:
            summary.net_cost !== null && summary.net_cost !== undefined
              ? String(summary.net_cost)
              : "",
        }));

        setPriceInputs((prev) => ({
          ...prev,
          [maidId]:
            summary.price !== null && summary.price !== undefined
              ? String(summary.price)
              : "",
        }));
      } catch (err: any) {
        console.error("Failed to load payments:", err);
      }
    },
    [clientId, token]
  );

  /* ============================================================
     LOAD CLIENT
  ============================================================ */

  useEffect(() => {
    if (!clientId || Number.isNaN(clientId)) {
      setMessage("No client selected.");

      setLoading(false);
      return;
    }

    const run = async () => {
      try {
        const res = await api.get(`/clients/${clientId}`, {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        });

        const c: ClientDetail = res.data.client;

        setClient(c);

        setClientName(c.client_name);

        setContactInfo(c.contact_info);

        const cache: Record<number, MaidEdit> = {};

        (c.maids || []).forEach((m) => {
          cache[m.maid_id] = {
            editing: false,

            maid_name: m.maid_name,

            agent_name: m.agent_name || "",

            arrival_date: m.arrival_date || "",

            insurance_start_date: m.insurance_start_date || "",
          };
        });

        setMaidEdits(cache);

        setMessage("");

        await loadDocuments();
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          (err.message === "Network Error"
            ? "Unable to reach the server."
            : "Failed to load client.");

        setMessage(msg);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [clientId, token, loadDocuments]);

  /* ============================================================
     LOAD PAYMENT SUMMARIES FOR ALL MAIDS
  ============================================================ */

  useEffect(() => {
    if (!client || !token) {
      return;
    }

    client.maids.forEach((maid) => {
      loadPayments(maid.maid_id);
    });
  }, [client, token, loadPayments]);

  /* ============================================================
     UPLOAD DOCUMENTS
  ============================================================ */

  const uploadDocuments = async () => {
    if (!client) {
      return;
    }

    if (documentFiles.length === 0) {
      setMessage("Please choose at least one file.");

      return;
    }

    const formData = new FormData();

    documentFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      setUploadingDocuments(true);

      setMessage("");

      await api.post(`/clients/${client.client_id}/documents`, formData, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });

      const uploadedCount = documentFiles.length;

      setDocumentFiles([]);

      setMessage(
        uploadedCount === 1
          ? "Document uploaded successfully."
          : `${uploadedCount} documents uploaded successfully.`
      );

      await loadDocuments();
    } catch (err: any) {
      console.error("Document upload failed:", err);

      setMessage(err?.response?.data?.message || "Failed to upload documents.");
    } finally {
      setUploadingDocuments(false);
    }
  };

  /* ============================================================
     VIEW DOCUMENT
  ============================================================ */

  const viewDocument = async (doc: ClientDocument) => {
    if (!client) {
      return;
    }

    try {
      setMessage("");

      const response = await api.get(
        `/clients/${client.client_id}/documents/${doc.document_id}/file`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,

          responseType: "blob",
        }
      );

      let contentType =
        response.headers["content-type"] || "application/octet-stream";

      /*
       * Some production responses are arriving as
       * application/octet-stream even when the actual file
       * is a browser-viewable image or PDF.
       *
       * When that happens, inspect the first bytes of the file
       * and determine the real format.
       */
      if (
        !contentType ||
        contentType === "application/octet-stream" ||
        contentType === "binary/octet-stream"
      ) {
        const sourceBlob = response.data as Blob;

        const bytes = new Uint8Array(
          await sourceBlob.slice(0, 16).arrayBuffer()
        );

        /*
         * JPEG
         * FF D8 FF
         */
        if (
          bytes.length >= 3 &&
          bytes[0] === 0xff &&
          bytes[1] === 0xd8 &&
          bytes[2] === 0xff
        ) {
          contentType = "image/jpeg";
        } else if (

        /*
         * PNG
         * 89 50 4E 47 0D 0A 1A 0A
         */
          bytes.length >= 8 &&
          bytes[0] === 0x89 &&
          bytes[1] === 0x50 &&
          bytes[2] === 0x4e &&
          bytes[3] === 0x47 &&
          bytes[4] === 0x0d &&
          bytes[5] === 0x0a &&
          bytes[6] === 0x1a &&
          bytes[7] === 0x0a
        ) {
          contentType = "image/png";
        } else if (

        /*
         * PDF
         * %PDF
         */
          bytes.length >= 4 &&
          bytes[0] === 0x25 &&
          bytes[1] === 0x50 &&
          bytes[2] === 0x44 &&
          bytes[3] === 0x46
        ) {
          contentType = "application/pdf";
        } else if (

        /*
         * GIF87a / GIF89a
         */
          bytes.length >= 6 &&
          bytes[0] === 0x47 &&
          bytes[1] === 0x49 &&
          bytes[2] === 0x46 &&
          bytes[3] === 0x38 &&
          (bytes[4] === 0x37 || bytes[4] === 0x39) &&
          bytes[5] === 0x61
        ) {
          contentType = "image/gif";
        } else if (

        /*
         * WEBP
         * RIFF....WEBP
         */
          bytes.length >= 12 &&
          bytes[0] === 0x52 &&
          bytes[1] === 0x49 &&
          bytes[2] === 0x46 &&
          bytes[3] === 0x46 &&
          bytes[8] === 0x57 &&
          bytes[9] === 0x45 &&
          bytes[10] === 0x42 &&
          bytes[11] === 0x50
        ) {
          contentType = "image/webp";
        }
      }

      console.log("Document:", doc.original_filename);
      console.log("Server Content-Type:", response.headers["content-type"]);
      console.log("Viewer Content-Type:", contentType);

      /*
       * Re-create the blob using the corrected MIME type.
       */
      const blob = new Blob([response.data], {
        type: contentType,
      });

      const url = URL.createObjectURL(blob);

      /*
       * Open the document in a new browser tab.
       *
       * Images will use the browser image viewer.
       * PDFs will use the browser PDF viewer.
       * The user can then download from the browser if desired.
       */
      const newWindow = window.open(url, "_blank");

      if (!newWindow) {
        URL.revokeObjectURL(url);

        setMessage(
          "The browser blocked the document window. Please allow pop-ups for BaytCare."
        );

        return;
      }

      /*
       * Keep the temporary blob URL available long enough
       * for the new tab to finish loading.
       */
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (err: any) {
      console.error("Failed to open document:", err);

      setMessage(err?.response?.data?.message || "Failed to open document.");
    }
  };

  /* ============================================================
     DOCUMENT RENAME
  ============================================================ */

  const startRenameDocument = (doc: ClientDocument) => {
    setDocumentEdits((prev) => ({
      ...prev,

      [doc.document_id]: {
        editing: true,
        name: doc.original_filename,
      },
    }));
  };

  const cancelRenameDocument = (doc: ClientDocument) => {
    setDocumentEdits((prev) => ({
      ...prev,

      [doc.document_id]: {
        editing: false,
        name: doc.original_filename,
      },
    }));
  };

  const saveDocumentName = async (doc: ClientDocument) => {
    if (!client) {
      return;
    }

    const edit = documentEdits[doc.document_id];

    if (!edit) {
      return;
    }

    const newName = edit.name.trim();

    if (!newName) {
      setMessage("Document name cannot be empty.");

      return;
    }

    try {
      setMessage("");

      await api.patch(
        `/clients/${client.client_id}/documents/${doc.document_id}`,
        {
          original_filename: newName,
        },
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        }
      );

      setDocumentEdits((prev) => ({
        ...prev,

        [doc.document_id]: {
          editing: false,
          name: newName,
        },
      }));

      await loadDocuments();

      setMessage("Document renamed successfully.");
    } catch (err: any) {
      console.error("Failed to rename document:", err);

      setMessage(err?.response?.data?.message || "Failed to rename document.");
    }
  };

  /* ============================================================
     DELETE DOCUMENT
  ============================================================ */

  const deleteDocument = async (doc: ClientDocument) => {
    if (!client) {
      return;
    }

    const confirmed = window.confirm(
      `Delete "${doc.original_filename}"? This cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setMessage("");

      await api.delete(
        `/clients/${client.client_id}/documents/${doc.document_id}`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        }
      );

      await loadDocuments();

      setMessage("Document deleted successfully.");
    } catch (err: any) {
      console.error("Failed to delete document:", err);

      setMessage(err?.response?.data?.message || "Failed to delete document.");
    }
  };

  /* ============================================================
     SAVE CLIENT
  ============================================================ */

  const saveClient = async () => {
    if (!client) {
      return;
    }

    const payload: any = {};

    if (clientName.trim() !== client.client_name) {
      payload.client_name = clientName.trim();
    }

    if (contactInfo.trim() !== client.contact_info) {
      payload.contact_info = contactInfo.trim();
    }

    if (Object.keys(payload).length === 0) {
      setEditingClient(false);
      return;
    }

    try {
      const res = await api.patch(`/clients/${client.client_id}`, payload, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });

      if (res.data?.client) {
        setClient((prev) =>
          prev
            ? {
                ...prev,

                client_name: res.data.client.client_name,

                contact_info: res.data.client.contact_info,
              }
            : prev
        );
      }

      setEditingClient(false);

      setMessage("Client info updated.");
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to save client.";

      setMessage(msg);
    }
  };

  /* ============================================================
     SAVE EXISTING MAID
  ============================================================ */

  const saveMaid = async (maid: Maid) => {
    if (!client) {
      return;
    }

    const local = maidEdits[maid.maid_id];

    if (!local) {
      return;
    }

    const cleanMaidName = local.maid_name.trim();

    const cleanAgentName = local.agent_name.trim();

    if (!cleanMaidName) {
      setMessage("Maid name cannot be empty.");

      return;
    }

    const payload: any = {
      maid_id: maid.maid_id,
    };

    if (cleanMaidName !== maid.maid_name) {
      payload.maid_name = cleanMaidName;
    }

    const existingAgent = maid.agent_name || "";

    if (cleanAgentName !== existingAgent) {
      payload.agent_name = cleanAgentName;
    }

    const existingArrival = maid.arrival_date || "";

    if (local.arrival_date !== existingArrival) {
      payload.arrival_date = local.arrival_date;
    }

    const existingInsuranceStart = maid.insurance_start_date || "";

    if (local.insurance_start_date !== existingInsuranceStart) {
      payload.insurance_start_date = local.insurance_start_date;
    }

    if (Object.keys(payload).length === 1) {
      setMaidEdits((s) => ({
        ...s,

        [maid.maid_id]: {
          ...local,
          editing: false,
        },
      }));

      return;
    }

    try {
      setMessage("");

      const res = await api.patch(`/clients/${client.client_id}`, payload, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });

      const updatedMaid: Maid = res.data.maid;

      setClient((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,

          maids: prev.maids.map((m) =>
            m.maid_id === maid.maid_id ? updatedMaid : m
          ),
        };
      });

      setMaidEdits((s) => ({
        ...s,

        [maid.maid_id]: {
          editing: false,

          maid_name: updatedMaid.maid_name,

          agent_name: updatedMaid.agent_name || "",

          arrival_date: updatedMaid.arrival_date || "",

          insurance_start_date: updatedMaid.insurance_start_date || "",
        },
      }));

      setMessage("Maid info updated.");
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to save maid.";

      setMessage(msg);
    }
  };

  /* ============================================================
     ADD NEW MAID
  ============================================================ */

  const addMaid = async () => {
    if (!client) {
      return;
    }

    const cleanMaidName = newMaidName.trim();

    const cleanAgentName = newAgentName.trim();

    if (!cleanMaidName) {
      setMessage("Maid name is required.");

      return;
    }

    if (!token) {
      setMessage("Your session has expired. Please sign in again.");

      return;
    }

    try {
      setAddingMaid(true);
      setMessage("");

      const res = await api.post(
        "/add-maid",
        {
          client_id: client.client_id,

          maid_name: cleanMaidName,

          agent_name: cleanAgentName || null,

          arrival_date: newArrivalDate || null,

          insurance_start_date: newInsuranceStartDate || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const newMaid: Maid = res.data.maid;

      /* Add new maid to the page immediately */

      setClient((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,

          maids: [newMaid, ...prev.maids],
        };
      });

      /* Add new maid to edit cache */

      setMaidEdits((prev) => ({
        ...prev,

        [newMaid.maid_id]: {
          editing: false,

          maid_name: newMaid.maid_name,

          agent_name: newMaid.agent_name || "",

          arrival_date: newMaid.arrival_date || "",

          insurance_start_date: newMaid.insurance_start_date || "",
        },
      }));

      /* Reset form */

      setNewMaidName("");
      setNewAgentName("");
      setNewArrivalDate("");
      setNewInsuranceStartDate("");

      setShowAddMaid(false);

      setMessage("Maid added successfully.");
    } catch (err: any) {
      console.error("Failed to add maid:", err);

      setMessage(err?.response?.data?.message || "Failed to add maid.");
    } finally {
      setAddingMaid(false);
    }
  };

  /* ============================================================
   DELETE MAID
============================================================ */

  const deleteMaid = async (maid: Maid) => {
    if (!client) {
      return;
    }

    const confirmed = window.confirm(
      `Delete maid "${maid.maid_name}"? This cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    if (!token) {
      setMessage("Your session has expired. Please sign in again.");

      return;
    }

    try {
      setMessage("");

      await api.delete(`/clients/${client.client_id}/maids/${maid.maid_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      /* Remove maid from page immediately */

      setClient((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,

          maids: prev.maids.filter((m) => m.maid_id !== maid.maid_id),
        };
      });

      /* Remove maid from edit cache */

      setMaidEdits((prev) => {
        const updated = {
          ...prev,
        };

        delete updated[maid.maid_id];

        return updated;
      });

      setMessage("Maid deleted successfully.");
    } catch (err: any) {
      console.error("Failed to delete maid:", err);

      setMessage(err?.response?.data?.message || "Failed to delete maid.");
    }
  };
  /* ============================================================
     DELETE CLIENT
  ============================================================ */

  const deleteClient = async () => {
    if (!client) {
      return;
    }

    const confirmed = window.confirm(
      "Delete this client and all their maids and documents? This cannot be undone."
    );

    if (!confirmed) {
      return;
    }

    try {
      await api.delete(`/clients/${client.client_id}`, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });

      setMessage("Client deleted.");

      setTimeout(() => {
        navigate("/clients");
      }, 800);
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Delete failed.";

      setMessage(msg);
    }
  };

  /* ============================================================
     SAVE NET COST
  ============================================================ */

  const saveNetCost = async (maidId: number) => {
    if (!client || !token) {
      setMessage("Your session has expired. Please sign in again.");
      return;
    }

    const value = netCostInputs[maidId];

    if (value === undefined || value.trim() === "") {
      setMessage("Please enter the net cost.");
      return;
    }

    const netCost = Number(value);

    if (!Number.isFinite(netCost) || netCost < 0) {
      setMessage("Net cost must be a valid non-negative number.");
      return;
    }

    try {
      setPaymentLoading((prev) => ({
        ...prev,
        [maidId]: true,
      }));

      setMessage("");

      await api.patch(
        `/clients/${client.client_id}/maids/${maidId}/net-cost`,
        {
          net_cost: netCost,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setClient((prev) =>
        prev
          ? {
              ...prev,
              maids: prev.maids.map((maid) =>
                maid.maid_id === maidId
                  ? {
                      ...maid,
                      net_cost: netCost,
                    }
                  : maid
              ),
            }
          : prev
      );

      await loadPayments(maidId);

      setMessage("Net cost saved successfully.");
    } catch (err: any) {
      console.error("Failed to save net cost:", err);

      setMessage(err?.response?.data?.message || "Failed to save net cost.");
    } finally {
      setPaymentLoading((prev) => ({
        ...prev,
        [maidId]: false,
      }));
    }
  };

  /* ============================================================
     SAVE CLIENT PRICE
  ============================================================ */

  const savePrice = async (maidId: number) => {
    if (!client || !token) {
      setMessage("Your session has expired. Please sign in again.");
      return;
    }

    const value = priceInputs[maidId];

    if (value === undefined || value.trim() === "") {
      setMessage("Please enter the client price.");
      return;
    }

    const price = Number(value);

    if (!Number.isFinite(price) || price < 0) {
      setMessage("Client price must be a valid non-negative number.");
      return;
    }

    try {
      setPaymentLoading((prev) => ({
        ...prev,
        [maidId]: true,
      }));

      setMessage("");

      await api.patch(
        `/clients/${client.client_id}/maids/${maidId}/price`,
        {
          price,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setClient((prev) =>
        prev
          ? {
              ...prev,
              maids: prev.maids.map((maid) =>
                maid.maid_id === maidId
                  ? {
                      ...maid,
                      price,
                    }
                  : maid
              ),
            }
          : prev
      );

      await loadPayments(maidId);

      setMessage("Client price saved successfully.");
    } catch (err: any) {
      console.error("Failed to save client price:", err);

      setMessage(
        err?.response?.data?.message || "Failed to save client price."
      );
    } finally {
      setPaymentLoading((prev) => ({
        ...prev,
        [maidId]: false,
      }));
    }
  };

  /* ============================================================
     ADD PAYMENT
  ============================================================ */

  const addPayment = async (maidId: number) => {
    if (!client || !token) {
      setMessage("Your session has expired. Please sign in again.");
      return;
    }

    const form = paymentForms[maidId];

    if (!form?.amount || !form?.payment_date) {
      setMessage("Enter both payment amount and payment date.");
      return;
    }

    const amount = Number(form.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      setMessage("Payment amount must be greater than zero.");
      return;
    }

    try {
      setPaymentLoading((prev) => ({
        ...prev,
        [maidId]: true,
      }));

      setMessage("");

      await api.post(
        `/clients/${client.client_id}/maids/${maidId}/payments`,
        {
          amount,
          payment_date: form.payment_date,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPaymentForms((prev) => ({
        ...prev,
        [maidId]: {
          amount: "",
          payment_date: "",
        },
      }));

      await loadPayments(maidId);

      setMessage("Payment added successfully.");
    } catch (err: any) {
      console.error("Failed to add payment:", err);

      setMessage(err?.response?.data?.message || "Failed to add payment.");
    } finally {
      setPaymentLoading((prev) => ({
        ...prev,
        [maidId]: false,
      }));
    }
  };

  /* ============================================================
     DELETE PAYMENT
  ============================================================ */

  const deletePayment = async (maidId: number, paymentId: number) => {
    if (!client || !token) {
      setMessage("Your session has expired. Please sign in again.");
      return;
    }

    const confirmed = window.confirm(
      "Delete this payment? This cannot be undone."
    );

    if (!confirmed) {
      return;
    }

    try {
      setPaymentLoading((prev) => ({
        ...prev,
        [maidId]: true,
      }));

      setMessage("");

      await api.delete(
        `/clients/${client.client_id}/maids/${maidId}/payments/${paymentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await loadPayments(maidId);

      setMessage("Payment deleted successfully.");
    } catch (err: any) {
      console.error("Failed to delete payment:", err);

      setMessage(err?.response?.data?.message || "Failed to delete payment.");
    } finally {
      setPaymentLoading((prev) => ({
        ...prev,
        [maidId]: false,
      }));
    }
  };

  /* ============================================================
     LOADING
  ============================================================ */

  if (loading) {
    return (
      <div className="create-client-container">
        <p>Loading…</p>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="create-client-container">
        <p>{message || "Client not found."}</p>
      </div>
    );
  }

  /* ============================================================
     PAGE
  ============================================================ */

  return (
    <div
      className="create-client-container"
      style={{
        paddingBottom: "120px",
      }}
    >
      <h2>Edit Client</h2>

      {/* ========================================================
          CLIENT
      ======================================================== */}

      <div className="card">
        <div className="card-header">
          <strong>Client Overview</strong>

          <span className="muted">Client : {client.client_name}</span>
        </div>

        {!editingClient ? (
          <div className="card-body">
            <div className="kv">
              <span>Name</span>

              <b>{client.client_name}</b>
            </div>

            <div className="kv">
              <span>Contact</span>

              <b>{client.contact_info}</b>
            </div>

            <div className="actions">
              <button onClick={() => setEditingClient(true)}>
                Edit Client
              </button>

              <button className="danger-btn" onClick={deleteClient}>
                Delete Client
              </button>
            </div>
          </div>
        ) : (
          <div className="card-body">
            <label>
              Client Name:
              <input
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            </label>

            <label>
              Contact Info:
              <input
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
              />
            </label>

            <div className="actions">
              <button onClick={saveClient}>Save</button>

              <button
                className="secondary-btn"
                onClick={() => {
                  setClientName(client.client_name);

                  setContactInfo(client.contact_info);

                  setEditingClient(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================
          DOCUMENTS
      ======================================================== */}

      <h3
        style={{
          marginTop: "1rem",
        }}
      >
        Documents
      </h3>

      <div className="card">
        <div className="card-header">
          <strong>Client Documents</strong>

          <span className="muted">
            {documents.length} {documents.length === 1 ? "file" : "files"}
          </span>
        </div>

        <div className="card-body">
          <label>
            Choose Documents:
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tif,.tiff,.heic,.heif,image/*,application/pdf"
              onChange={(e) => {
                setDocumentFiles(Array.from(e.target.files || []));
              }}
            />
          </label>

          {documentFiles.length > 0 && (
            <div
              style={{
                marginTop: "12px",
              }}
            >
              <strong>Selected files:</strong>

              <div
                style={{
                  marginTop: "8px",
                }}
              >
                {documentFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    style={{
                      padding: "4px 0",
                    }}
                  >
                    📎 {file.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="actions">
            <button
              onClick={uploadDocuments}
              disabled={uploadingDocuments || documentFiles.length === 0}
            >
              {uploadingDocuments
                ? "Uploading..."
                : documentFiles.length > 1
                ? `Upload ${documentFiles.length} Files`
                : "Upload File"}
            </button>
          </div>

          <div
            style={{
              marginTop: "20px",
            }}
          >
            {loadingDocuments ? (
              <p>Loading documents...</p>
            ) : documents.length === 0 ? (
              <p className="muted">No documents uploaded for this client.</p>
            ) : (
              <div>
                {documents.map((doc) => {
                  const edit = documentEdits[doc.document_id];

                  const editing = edit?.editing ?? false;

                  return (
                    <div
                      key={doc.document_id}
                      style={{
                        padding: "14px 0",

                        borderBottom: "1px solid #eee",
                      }}
                    >
                      {!editing ? (
                        <>
                          <div className="kv">
                            <span>📄 {doc.original_filename}</span>

                            <span className="muted">
                              {doc.uploaded_at
                                ? new Date(doc.uploaded_at).toLocaleDateString()
                                : ""}
                            </span>
                          </div>

                          <div className="actions">
                            <button onClick={() => viewDocument(doc)}>
                              View
                            </button>

                            <button
                              className="secondary-btn"
                              onClick={() => startRenameDocument(doc)}
                            >
                              Rename
                            </button>

                            <button
                              className="danger-btn"
                              onClick={() => deleteDocument(doc)}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <label>
                            File Name:
                            <input
                              value={edit.name}
                              onChange={(e) =>
                                setDocumentEdits((prev) => ({
                                  ...prev,

                                  [doc.document_id]: {
                                    editing: true,

                                    name: e.target.value,
                                  },
                                }))
                              }
                            />
                          </label>

                          <div className="actions">
                            <button onClick={() => saveDocumentName(doc)}>
                              Save Name
                            </button>

                            <button
                              className="secondary-btn"
                              onClick={() => cancelRenameDocument(doc)}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ========================================================
          MAIDS
      ======================================================== */}

      <h3
        style={{
          marginTop: "1rem",
        }}
      >
        Maids
      </h3>

      {/* ========================================================
          ADD MAID CARD
      ======================================================== */}

      <div
        className="card"
        style={{
          marginBottom: "1rem",
        }}
      >
        <div className="card-header">
          <strong>Add Maid</strong>

          <span className="muted">Add another maid to this client</span>
        </div>

        <div className="card-body">
          {!showAddMaid ? (
            <div className="actions">
              <button
                type="button"
                onClick={() => {
                  setShowAddMaid(true);

                  setMessage("");
                }}
              >
                + Add Maid
              </button>
            </div>
          ) : (
            <>
              <label>
                Maid Name:
                <input
                  type="text"
                  value={newMaidName}
                  placeholder="Maid name"
                  onChange={(e) => setNewMaidName(e.target.value)}
                />
              </label>

              <label>
                Agent Name:
                <input
                  type="text"
                  value={newAgentName}
                  placeholder="Optional"
                  onChange={(e) => setNewAgentName(e.target.value)}
                />
              </label>

              <label>
                Arrival Date:
                <input
                  type="date"
                  value={newArrivalDate}
                  onChange={(e) => setNewArrivalDate(e.target.value)}
                />
              </label>

              <label>
                Insurance Start Date:
                <input
                  type="date"
                  value={newInsuranceStartDate}
                  onChange={(e) => setNewInsuranceStartDate(e.target.value)}
                />
              </label>

              <p className="muted">
                Insurance renewal reminder is calculated automatically 360 days
                after the start date.
              </p>

              <p className="muted">
                Leave the arrival date empty if the maid has not arrived yet.
              </p>

              <div className="actions">
                <button type="button" onClick={addMaid} disabled={addingMaid}>
                  {addingMaid ? "Adding..." : "Add Maid"}
                </button>

                <button
                  type="button"
                  className="secondary-btn"
                  disabled={addingMaid}
                  onClick={() => {
                    setNewMaidName("");

                    setNewAgentName("");

                    setNewArrivalDate("");
                    setNewInsuranceStartDate("");

                    setShowAddMaid(false);

                    setMessage("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ========================================================
          EXISTING MAIDS
      ======================================================== */}

      {newestFirstMaids.length === 0 && <p className="muted">No maids yet.</p>}

      <div
        className="maids-grid"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          width: "100%",
        }}
      >
        {newestFirstMaids.map((m) => {
          const local = maidEdits[m.maid_id];

          const editing = local?.editing ?? false;

          return (
            <div
              key={m.maid_id}
              className="maid-card"
              style={{
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setExpandedMaidId((current) =>
                    current === m.maid_id ? null : m.maid_id
                  )
                }
                aria-expanded={expandedMaidId === m.maid_id}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius:
                    expandedMaidId === m.maid_id ? "10px 10px 0 0" : "10px",
                  background: "#007bff",
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "14px 16px",
                  color: "#ffffff",
                  fontSize: "1rem",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: 0,
                  }}
                >
                  <strong
                    style={{
                      color: "#ffffff",
                      fontSize: "1rem",
                      fontWeight: 700,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.maid_name || "Unnamed Maid"}
                  </strong>
                </span>

                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      color: m.status === "arrived" ? "#146c43" : "#b02a37",
                      background:
                        m.status === "arrived" ? "#d1e7dd" : "#f8d7da",
                      borderRadius: "999px",
                      padding: "4px 9px",
                    }}
                  >
                    {m.status === "arrived" ? "Arrived" : "Have Not Arrived"}
                  </span>

                  <span
                    aria-hidden="true"
                    style={{
                      color: "#ffffff",
                      fontWeight: 700,
                    }}
                  >
                    {expandedMaidId === m.maid_id ? "▼" : "▶"}
                  </span>
                </span>
              </button>

              {expandedMaidId === m.maid_id && (
                <div
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e5e5e5",
                    borderTop: "none",
                    borderRadius: "0 0 10px 10px",
                    padding: "16px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      marginBottom: "14px",
                      color: "#333333",
                    }}
                  >
                    More Info
                  </div>

                  {!editing ? (
                    <div className="card-body">
                      <div className="kv">
                        <span>Maid</span>

                        <b>{m.maid_name}</b>
                      </div>

                      <div className="kv">
                        <span>Agent</span>

                        <b>{m.agent_name || "—"}</b>
                      </div>

                      <div className="kv">
                        <span>Status</span>

                        <b>
                          {m.status === "arrived"
                            ? "Arrived"
                            : "Have Not Arrived"}
                        </b>
                      </div>

                      <div className="kv">
                        <span>Arrival</span>

                        <b>{m.arrival_date || "—"}</b>
                      </div>

                      <div className="kv">
                        <span>Reminder +2m</span>

                        <b>{m.reminder_2_months || "—"}</b>
                      </div>

                      <div className="kv">
                        <span>Reminder +14m</span>

                        <b>{m.reminder_14_months || "—"}</b>
                      </div>

                      <div className="kv">
                        <span>Insurance Start</span>

                        <b>{m.insurance_start_date || "—"}</b>
                      </div>

                      <div className="kv">
                        <span>Insurance Renewal (360 days)</span>

                        <b>{m.insurance_renewal_date || "—"}</b>
                      </div>

                      {/* =====================================================
                      PAYMENTS
                  ===================================================== */}

                      <div
                        className="payment-section"
                        style={{
                          marginTop: "20px",
                          paddingTop: "18px",
                          borderTop: "1px solid #e5e5e5",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                            flexWrap: "wrap",
                          }}
                        >
                          <h3 style={{ margin: 0 }}>Payments</h3>

                          {(() => {
                            const summary = paymentSummaries[m.maid_id];

                            if (
                              !summary ||
                              summary.amount_due === null ||
                              summary.price === null ||
                              summary.amount_due > 0
                            ) {
                              return null;
                            }

                            return (
                              <span
                                style={{
                                  padding: "5px 10px",
                                  border: "1px solid #d6d6d6",
                                  borderRadius: "999px",
                                  fontSize: "0.85rem",
                                  fontWeight: 700,
                                }}
                              >
                                Paid in Full
                              </span>
                            );
                          })()}
                        </div>

                        <p className="muted" style={{ marginTop: "6px" }}>
                          Net Cost is what the agency paid. Client Price is what
                          the client is charged. Profit is Client Price minus
                          Net Cost.
                        </p>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(140px, 1fr))",
                            gap: "10px",
                            marginTop: "16px",
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid #e5e5e5",
                              borderRadius: "10px",
                              padding: "12px",
                            }}
                          >
                            <div className="muted">Net Cost</div>
                            <strong>
                              {paymentSummaries[m.maid_id]?.net_cost !== null &&
                              paymentSummaries[m.maid_id]?.net_cost !==
                                undefined
                                ? Number(
                                    paymentSummaries[m.maid_id].net_cost
                                  ).toFixed(2)
                                : "—"}
                            </strong>
                          </div>

                          <div
                            style={{
                              border: "1px solid #e5e5e5",
                              borderRadius: "10px",
                              padding: "12px",
                            }}
                          >
                            <div className="muted">Client Price</div>
                            <strong>
                              {paymentSummaries[m.maid_id]?.price !== null &&
                              paymentSummaries[m.maid_id]?.price !== undefined
                                ? Number(
                                    paymentSummaries[m.maid_id].price
                                  ).toFixed(2)
                                : "—"}
                            </strong>
                          </div>

                          <div
                            style={{
                              border: "1px solid #e5e5e5",
                              borderRadius: "10px",
                              padding: "12px",
                            }}
                          >
                            <div className="muted">Profit</div>
                            <strong>
                              {paymentSummaries[m.maid_id]?.profit !== null &&
                              paymentSummaries[m.maid_id]?.profit !== undefined
                                ? Number(
                                    paymentSummaries[m.maid_id].profit
                                  ).toFixed(2)
                                : "—"}
                            </strong>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(180px, 1fr))",
                            gap: "10px",
                            marginTop: "10px",
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid #e5e5e5",
                              borderRadius: "10px",
                              padding: "12px",
                            }}
                          >
                            <div className="muted">Total Paid by Client</div>
                            <strong>
                              {Number(
                                paymentSummaries[m.maid_id]?.total_paid || 0
                              ).toFixed(2)}
                            </strong>
                          </div>

                          <div
                            style={{
                              border: "1px solid #e5e5e5",
                              borderRadius: "10px",
                              padding: "12px",
                            }}
                          >
                            <div className="muted">Balance Due</div>
                            <strong>
                              {paymentSummaries[m.maid_id]?.amount_due !==
                                null &&
                              paymentSummaries[m.maid_id]?.amount_due !==
                                undefined
                                ? Number(
                                    paymentSummaries[m.maid_id].amount_due
                                  ).toFixed(2)
                                : "—"}
                            </strong>
                          </div>
                        </div>

                        <details style={{ marginTop: "18px" }}>
                          <summary
                            style={{
                              cursor: "pointer",
                              fontWeight: 700,
                            }}
                          >
                            Edit Pricing
                          </summary>

                          <div style={{ marginTop: "14px" }}>
                            <label>
                              Net Cost Paid by Agency:
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Agency cost"
                                value={
                                  netCostInputs[m.maid_id] ??
                                  (m.net_cost !== null &&
                                  m.net_cost !== undefined
                                    ? String(m.net_cost)
                                    : "")
                                }
                                onChange={(e) =>
                                  setNetCostInputs((prev) => ({
                                    ...prev,
                                    [m.maid_id]: e.target.value,
                                  }))
                                }
                              />
                            </label>

                            <div className="actions">
                              <button
                                type="button"
                                onClick={() => saveNetCost(m.maid_id)}
                                disabled={paymentLoading[m.maid_id]}
                              >
                                {paymentLoading[m.maid_id]
                                  ? "Saving..."
                                  : "Save Net Cost"}
                              </button>
                            </div>

                            <label style={{ marginTop: "14px" }}>
                              Client Price:
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Price charged to client"
                                value={
                                  priceInputs[m.maid_id] ??
                                  (m.price !== null && m.price !== undefined
                                    ? String(m.price)
                                    : "")
                                }
                                onChange={(e) =>
                                  setPriceInputs((prev) => ({
                                    ...prev,
                                    [m.maid_id]: e.target.value,
                                  }))
                                }
                              />
                            </label>

                            <div className="actions">
                              <button
                                type="button"
                                onClick={() => savePrice(m.maid_id)}
                                disabled={paymentLoading[m.maid_id]}
                              >
                                {paymentLoading[m.maid_id]
                                  ? "Saving..."
                                  : "Save Client Price"}
                              </button>
                            </div>
                          </div>
                        </details>

                        <details style={{ marginTop: "18px" }}>
                          <summary
                            style={{
                              cursor: "pointer",
                              fontWeight: 700,
                            }}
                          >
                            + Add Payment
                          </summary>

                          <div style={{ marginTop: "14px" }}>
                            <label>
                              Amount Paid by Client:
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                placeholder="Payment amount"
                                value={paymentForms[m.maid_id]?.amount || ""}
                                onChange={(e) =>
                                  setPaymentForms((prev) => ({
                                    ...prev,
                                    [m.maid_id]: {
                                      ...(prev[m.maid_id] || {
                                        amount: "",
                                        payment_date: "",
                                      }),
                                      amount: e.target.value,
                                    },
                                  }))
                                }
                              />
                            </label>

                            <label>
                              Payment Date:
                              <input
                                type="date"
                                value={
                                  paymentForms[m.maid_id]?.payment_date || ""
                                }
                                onChange={(e) =>
                                  setPaymentForms((prev) => ({
                                    ...prev,
                                    [m.maid_id]: {
                                      ...(prev[m.maid_id] || {
                                        amount: "",
                                        payment_date: "",
                                      }),
                                      payment_date: e.target.value,
                                    },
                                  }))
                                }
                              />
                            </label>

                            <div className="actions">
                              <button
                                type="button"
                                onClick={() => addPayment(m.maid_id)}
                                disabled={paymentLoading[m.maid_id]}
                              >
                                {paymentLoading[m.maid_id]
                                  ? "Saving..."
                                  : "Add Payment"}
                              </button>
                            </div>
                          </div>
                        </details>

                        <h4
                          style={{
                            marginTop: "20px",
                            marginBottom: "10px",
                          }}
                        >
                          Payment History
                        </h4>

                        {paymentSummaries[m.maid_id]?.payments?.length ? (
                          <div style={{ overflowX: "auto" }}>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                              }}
                            >
                              <thead>
                                <tr>
                                  <th
                                    style={{
                                      textAlign: "left",
                                      padding: "8px",
                                      borderBottom: "1px solid #e5e5e5",
                                    }}
                                  >
                                    Date
                                  </th>
                                  <th
                                    style={{
                                      textAlign: "left",
                                      padding: "8px",
                                      borderBottom: "1px solid #e5e5e5",
                                    }}
                                  >
                                    Payment
                                  </th>
                                  <th
                                    style={{
                                      textAlign: "right",
                                      padding: "8px",
                                      borderBottom: "1px solid #e5e5e5",
                                    }}
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {paymentSummaries[m.maid_id].payments.map(
                                  (payment) => (
                                    <tr key={payment.payment_id}>
                                      <td
                                        style={{
                                          padding: "8px",
                                          borderBottom: "1px solid #f0f0f0",
                                        }}
                                      >
                                        {payment.payment_date}
                                      </td>

                                      <td
                                        style={{
                                          padding: "8px",
                                          borderBottom: "1px solid #f0f0f0",
                                        }}
                                      >
                                        <strong>
                                          {Number(payment.amount).toFixed(2)}
                                        </strong>
                                      </td>

                                      <td
                                        style={{
                                          padding: "8px",
                                          textAlign: "right",
                                          borderBottom: "1px solid #f0f0f0",
                                        }}
                                      >
                                        <button
                                          type="button"
                                          className="danger-btn"
                                          onClick={() =>
                                            deletePayment(
                                              m.maid_id,
                                              payment.payment_id
                                            )
                                          }
                                          disabled={paymentLoading[m.maid_id]}
                                        >
                                          Delete
                                        </button>
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="muted">No payments recorded yet.</p>
                        )}
                      </div>

                      <div className="actions">
                        <button
                          onClick={() =>
                            setMaidEdits((s) => ({
                              ...s,

                              [m.maid_id]: {
                                editing: true,

                                maid_name: m.maid_name,

                                agent_name: m.agent_name || "",

                                arrival_date: m.arrival_date || "",

                                insurance_start_date:
                                  m.insurance_start_date || "",
                              },
                            }))
                          }
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="danger-btn"
                          onClick={() => deleteMaid(m)}
                        >
                          Delete Maid
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="card-body">
                      <label>
                        Maid Name:
                        <input
                          value={local?.maid_name || ""}
                          onChange={(e) =>
                            setMaidEdits((s) => ({
                              ...s,

                              [m.maid_id]: {
                                ...s[m.maid_id],

                                maid_name: e.target.value,
                              },
                            }))
                          }
                        />
                      </label>

                      <label>
                        Agent Name:
                        <input
                          type="text"
                          value={local?.agent_name || ""}
                          placeholder="Optional"
                          onChange={(e) =>
                            setMaidEdits((s) => ({
                              ...s,

                              [m.maid_id]: {
                                ...s[m.maid_id],

                                agent_name: e.target.value,
                              },
                            }))
                          }
                        />
                      </label>

                      <label>
                        Arrival Date:
                        <input
                          type="date"
                          value={local?.arrival_date || ""}
                          onChange={(e) =>
                            setMaidEdits((s) => ({
                              ...s,

                              [m.maid_id]: {
                                ...s[m.maid_id],

                                arrival_date: e.target.value,
                              },
                            }))
                          }
                        />
                      </label>

                      <label>
                        Insurance Start Date:
                        <input
                          type="date"
                          value={local?.insurance_start_date || ""}
                          onChange={(e) =>
                            setMaidEdits((s) => ({
                              ...s,

                              [m.maid_id]: {
                                ...s[m.maid_id],

                                insurance_start_date: e.target.value,
                              },
                            }))
                          }
                        />
                      </label>

                      <p className="muted">
                        Renewal reminder is automatically set to 360 days after
                        the insurance start date.
                      </p>

                      <p className="muted">
                        Leave the arrival date empty if the maid has not
                        arrived.
                      </p>

                      <div className="actions">
                        <button onClick={() => saveMaid(m)}>Save</button>

                        <button
                          className="secondary-btn"
                          onClick={() =>
                            setMaidEdits((s) => ({
                              ...s,

                              [m.maid_id]: {
                                editing: false,

                                maid_name: m.maid_name,

                                agent_name: m.agent_name || "",

                                arrival_date: m.arrival_date || "",

                                insurance_start_date:
                                  m.insurance_start_date || "",
                              },
                            }))
                          }
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {message && <p className="feedback">{message}</p>}
    </div>
  );
};

export default EditClient;
